/**
 * 🛒 SHARED CART INTERFACES
 *
 * Diese Interfaces werden von Server UND Client verwendet.
 * Sie sind kompatibel mit:
 * - main/shared/api/orderPricing.ts (OrderItemRequest)
 * - main/shared/services/orderApi.ts (OrderItem)
 *
 * Änderungen hier müssen in beiden Kontexten kompatibel sein!
 */
// ================================
// CART TO ORDER CONVERSION
// ================================
/**
 * Convert CartItem to OrderItemRequest for server communication
 */
export function cartItemToOrderRequest(item) {
    if (item.itemType === 'menu') {
        return {
            id: item.menuId || item.id,
            itemType: 'menu',
            menuId: item.menuId || item.id,
            quantity: item.quantity,
            componentConfigs: (item.componentConfigs || []).map((config) => ({
                productId: config.productId,
                selectedSizeId: config.selectedSize?.id,
                extraIngredientIds: config.extras?.map((e) => e.id),
                removedIngredientIds: config.removedIngredients?.map((ingredient) => typeof ingredient === 'string' ? ingredient : ingredient.id),
                selectedMandatoryIngredients: config.selectedMandatoryIngredients,
                specialInstructions: config.specialInstructions
            }))
        };
    }
    return {
        id: item.id,
        itemType: 'product',
        quantity: item.quantity,
        selectedSizeId: item.selectedSize?.id,
        extraIngredients: item.extras?.map(extra => ({
            id: extra.id,
            name: extra.name,
            price: extra.price,
            priceCent: extra.priceCent,
            category: extra.category
        })),
        removedIngredients: item.removedIngredients?.map(ingredient => typeof ingredient === 'string' ? ingredient : ingredient.id),
        selectedMandatoryIngredients: item.selectedMandatoryIngredients?.map(group => ({
            groupId: group.groupId || group.groupName, // Fallback to groupName if no groupId
            groupName: group.groupName,
            selectedIngredients: group.selectedIngredients.map(ing => ({
                ingredientId: ing.ingredientId,
                name: ing.name
            }))
        })),
        specialInstructions: item.specialInstructions
    };
}
/**
 * Generate unique configuration ID for cart items
 * Used to distinguish items with same product but different configurations
 */
export function generateCartConfigId(item) {
    if (item.itemType === 'menu') {
        const menuId = item.menuId || item._id || item.id || 'unknown';
        const compKey = (item.componentConfigs || [])
            .map((c) => {
            const extrasIds = c.extras?.map((e) => e.id).sort().join(',') || '';
            const removedIds = c.removedIngredients
                ?.map((r) => (typeof r === 'string' ? r : r.id))
                .sort()
                .join(',') || '';
            const sizeId = c.selectedSize?.id || '';
            const mandatoryIds = c.selectedMandatoryIngredients
                ? c.selectedMandatoryIngredients
                    .map((g) => `${g.groupId || g.groupName}:${g.selectedIngredients.map((i) => i.ingredientId).sort().join(',')}`)
                    .sort()
                    .join('|')
                : '';
            return `${c.productId}_${sizeId}_${extrasIds}_${removedIds}_${mandatoryIds}_${c.specialInstructions || ''}`;
        })
            .join('||');
        return `menu_${menuId}_${compKey}`.replace(/\s+/g, '_');
    }
    const productId = item._id || item.id || 'unknown';
    const extrasIds = item.extras ? item.extras.map(e => e.id).sort().join(',') : '';
    const removedIds = item.removedIngredients
        ? item.removedIngredients
            .map(r => typeof r === 'string' ? r : r.id)
            .sort()
            .join(',')
        : '';
    const instructions = item.specialInstructions || '';
    const sizeId = item.selectedSize ? item.selectedSize.id : '';
    const mandatoryIds = item.selectedMandatoryIngredients
        ? item.selectedMandatoryIngredients
            .map(g => `${g.groupId || g.groupName}:${g.selectedIngredients.map(i => i.ingredientId).sort().join(',')}`)
            .sort()
            .join('|')
        : '';
    return `${productId}_${extrasIds}_${removedIds}_${sizeId}_${mandatoryIds}_${instructions}`.replace(/\s+/g, '_');
}
/**
 * Validate cart before checkout
 */
export function validateCart(items) {
    const errors = [];
    const warnings = [];
    if (items.length === 0) {
        errors.push('Warenkorb ist leer');
    }
    items.forEach((item, index) => {
        if (item.quantity <= 0) {
            errors.push(`Artikel ${index + 1}: Ungültige Menge`);
        }
        if (!item.menuItem.isAvailable) {
            warnings.push(`${item.menuItem.name} ist möglicherweise nicht verfügbar`);
        }
        if (item.itemType !== 'menu' && item.totalPrice <= 0) {
            errors.push(`${item.menuItem.name}: Ungültiger Preis`);
        }
        if (item.itemType === 'menu' && item.totalPrice < 0) {
            errors.push(`${item.menuItem.name}: Ungültiger Menüpreis`);
        }
    });
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}
